import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Crown,
  Mail,
  Calendar,
  Building,
  Home,
  DoorOpen,
  Eye,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';

const AdminLandlords = () => {
  const { t } = useI18n();
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonateData, setImpersonateData] = useState(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  useEffect(() => { loadLandlords(); }, []);

  const loadLandlords = async () => {
    try {
      const response = await adminAPI.getAllLandlords();
      setLandlords(response.data);
    } catch (error) {
      toast.error('Failed to load landlords');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionUpdate = async (landlordId, tier) => {
    try {
      await adminAPI.updateSubscription(landlordId, tier);
      toast.success('Subscription updated');
      loadLandlords();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleImpersonate = async (landlordId) => {
    setImpersonateLoading(true);
    try {
      const res = await adminAPI.impersonate(landlordId);
      setImpersonateData(res.data);
    } catch {
      toast.error('Failed to load landlord data');
    } finally {
      setImpersonateLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'ENTERPRISE': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'EXPERT': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'PRO': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'BASIC': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'trial': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <DashboardLayout type="admin" title={t('admin.landlords')}>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="admin" title={t('admin.landlords')}>
      <div className="space-y-6" data-testid="admin-landlords-page">
        <div>
          <p className="text-muted-foreground">Manage landlord accounts and subscriptions</p>
        </div>

        {landlords.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Crown className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Landlords Yet</h3>
              <p className="text-muted-foreground">Landlords will appear here when they register</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {landlords.map((landlord, index) => (
              <Card
                key={landlord.id}
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`landlord-card-${landlord.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 bg-gold/10 flex items-center justify-center shrink-0">
                        <Crown className="w-6 h-6 text-gold" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium">{landlord.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {landlord.email}
                          </span>
                          {landlord.organisation_name && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3.5 h-3.5" />
                              {landlord.organisation_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(landlord.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Home className="w-3.5 h-3.5" />
                        <span>{landlord.property_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <DoorOpen className="w-3.5 h-3.5" />
                        <span>{landlord.unit_count || 0}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getTierColor(landlord.subscription_tier)}`}>
                          {landlord.subscription_tier || 'BASIC'}
                        </span>
                        <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(landlord.subscription_status)}`}>
                          {landlord.subscription_status || 'trial'}
                        </span>
                      </div>
                      
                      <Select
                        value={landlord.subscription_tier || 'BASIC'}
                        onValueChange={(tier) => handleSubscriptionUpdate(landlord.id, tier)}
                      >
                        <SelectTrigger className="w-32 bg-background border-[#e5e7eb]" data-testid={`tier-select-${landlord.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BASIC">Basic</SelectItem>
                          <SelectItem value="PRO">Pro</SelectItem>
                          <SelectItem value="EXPERT">Expert</SelectItem>
                          <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleImpersonate(landlord.id)}
                        className="border-gold/30 text-gold hover:bg-gold/10"
                        data-testid={`view-as-${landlord.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View As
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Subscription Tiers Info */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <h3 className="font-serif text-lg mb-4">Subscription Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-gray-500/10 text-gray-400 border border-gray-500/20">BASIC</span>
                  <span className="font-semibold">$29/mo</span>
                </div>
                <p className="text-sm text-muted-foreground">1 property, up to 2 units</p>
              </div>
              <div className="p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20">PRO</span>
                  <span className="font-semibold">$59/mo</span>
                </div>
                <p className="text-sm text-muted-foreground">1 property, up to 5 units</p>
              </div>
              <div className="p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">EXPERT</span>
                  <span className="font-semibold">$99/mo</span>
                </div>
                <p className="text-sm text-muted-foreground">5 properties, up to 20 units</p>
              </div>
              <div className="p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-purple-500/10 text-purple-400 border border-purple-500/20">ENTERPRISE</span>
                  <span className="font-semibold">$299/mo</span>
                </div>
                <p className="text-sm text-muted-foreground">Unlimited properties and units</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Impersonate Dialog */}
        <Dialog open={!!impersonateData} onOpenChange={(open) => { if (!open) setImpersonateData(null); }}>
          <DialogContent className="sm:max-w-[700px] bg-card border-border max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                View As: {impersonateData?.landlord?.name}
              </DialogTitle>
            </DialogHeader>
            {impersonateData && (
              <div className="space-y-6 mt-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-background border border-border">
                    <p className="text-xs text-muted-foreground uppercase">Properties</p>
                    <p className="text-xl font-semibold">{impersonateData.summary.total_properties}</p>
                  </div>
                  <div className="p-3 bg-background border border-border">
                    <p className="text-xs text-muted-foreground uppercase">Units</p>
                    <p className="text-xl font-semibold">{impersonateData.summary.total_units}</p>
                  </div>
                  <div className="p-3 bg-background border border-border">
                    <p className="text-xs text-muted-foreground uppercase">Tenants</p>
                    <p className="text-xl font-semibold">{impersonateData.summary.total_tenants}</p>
                  </div>
                  <div className="p-3 bg-background border border-border">
                    <p className="text-xs text-muted-foreground uppercase">Active Leases</p>
                    <p className="text-xl font-semibold">{impersonateData.summary.active_leases}</p>
                  </div>
                  <div className="p-3 bg-background border border-border">
                    <p className="text-xs text-muted-foreground uppercase">Total Revenue</p>
                    <p className="text-xl font-semibold text-emerald-400">${impersonateData.summary.total_revenue.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-background border border-border">
                    <p className="text-xs text-muted-foreground uppercase">Pending Revenue</p>
                    <p className="text-xl font-semibold text-amber-400">${impersonateData.summary.pending_revenue.toLocaleString()}</p>
                  </div>
                </div>

                {/* Properties */}
                <div>
                  <h4 className="font-serif text-base mb-2">Properties</h4>
                  <div className="space-y-2">
                    {impersonateData.properties.map(p => (
                      <div key={p.id} className="px-4 py-2 bg-background border border-border flex justify-between items-center">
                        <span>{p.name}</span>
                        <span className="text-sm text-muted-foreground">{p.address}</span>
                      </div>
                    ))}
                    {impersonateData.properties.length === 0 && <p className="text-sm text-muted-foreground">No properties</p>}
                  </div>
                </div>

                {/* Tenants */}
                <div>
                  <h4 className="font-serif text-base mb-2">Tenants</h4>
                  <div className="space-y-2">
                    {impersonateData.tenants.map(t => (
                      <div key={t.id} className="px-4 py-2 bg-background border border-border flex justify-between items-center">
                        <span>{t.name}</span>
                        <span className="text-sm text-muted-foreground">{t.email}</span>
                      </div>
                    ))}
                    {impersonateData.tenants.length === 0 && <p className="text-sm text-muted-foreground">No tenants</p>}
                  </div>
                </div>

                {/* Recent Payments */}
                <div>
                  <h4 className="font-serif text-base mb-2">Recent Payments</h4>
                  <div className="space-y-2">
                    {impersonateData.payments.slice(0, 10).map(p => (
                      <div key={p.id} className="px-4 py-2 bg-background border border-border flex justify-between items-center">
                        <span>{p.description}</span>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 text-xs uppercase border ${p.status === 'PAID' ? 'text-emerald-400 border-emerald-500/20' : 'text-amber-400 border-amber-500/20'}`}>
                            {p.status}
                          </span>
                          <span className="font-medium">${p.amount.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                    {impersonateData.payments.length === 0 && <p className="text-sm text-muted-foreground">No payments</p>}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminLandlords;
